import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import SearchBar from "shared/SearchBar";
import { Button, ButtonPurpose, ButtonSize } from "shared/Button";
import SpeakerAssignRow from "../SpeakerAssignRow";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import styles from "./AssignSpeakersSection.module.css";

const noop = () => {};

const reorderByIds = (items, sourceId, targetId, position = "below") => {
  const sourceIndex = items.findIndex((item) => item.id === sourceId);

  if (sourceIndex < 0 || sourceId === targetId) {
    return items;
  }

  const nextItems = [...items];
  const [moved] = nextItems.splice(sourceIndex, 1);
  const targetIndex = nextItems.findIndex((item) => item.id === targetId);

  if (targetIndex < 0) {
    return items;
  }

  const insertionIndex = position === "above" ? targetIndex : targetIndex + 1;
  nextItems.splice(insertionIndex, 0, moved);
  return nextItems;
};

export function AssignSpeakersSection({
  speakers,
  onSearch,
  onAdd,
  onRemove,
  onReorder,
  onSpeakerVariationsChange,
  addDisabled,
  addDisabledReason,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [draggingSpeakerId, setDraggingSpeakerId] = useState(null);
  const [dropTargetSpeakerId, setDropTargetSpeakerId] = useState(null);
  const [dropTargetPosition, setDropTargetPosition] = useState(null);

  const filteredSpeakers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return speakers;
    }

    return speakers.filter((speaker) =>
      speaker.name.toLowerCase().includes(normalizedSearch),
    );
  }, [speakers, searchTerm]);

  const hasAssignedSpeakers = speakers.length > 0;
  const showNoMatches = hasAssignedSpeakers && filteredSpeakers.length === 0;

  const handleSearchInput = (value) => {
    setSearchTerm(value);
    onSearch(value);
  };

  const handleDragStart = (speakerId) => {
    setDraggingSpeakerId(speakerId);
  };

  const handleDragOver = (event, targetSpeakerId) => {
    event.preventDefault();

    if (draggingSpeakerId && draggingSpeakerId !== targetSpeakerId) {
      const targetRect = event.currentTarget.getBoundingClientRect();
      const isAboveMiddle =
        event.clientY < targetRect.top + targetRect.height / 2;

      setDropTargetSpeakerId(targetSpeakerId);
      setDropTargetPosition(isAboveMiddle ? "above" : "below");
    }
  };

  const handleDrop = (targetSpeakerId) => {
    if (!draggingSpeakerId || draggingSpeakerId === targetSpeakerId) {
      return;
    }

    const reordered = reorderByIds(
      speakers,
      draggingSpeakerId,
      targetSpeakerId,
      dropTargetPosition || "below",
    );
    onReorder(reordered);
    setDraggingSpeakerId(null);
    setDropTargetSpeakerId(null);
    setDropTargetPosition(null);
  };

  const handleDragEnd = () => {
    setDraggingSpeakerId(null);
    setDropTargetSpeakerId(null);
    setDropTargetPosition(null);
  };

  return (
    <section className={styles.card}>
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <h3 className={styles.title}>Assign Speakers</h3>
          <div className={styles.controlsRow}>
            <div className={styles.searchWrap}>
              <SearchBar
                placeholderText="Search Speakers"
                onInput={handleSearchInput}
                searchDelay={0}
                minChar={0}
              />
            </div>
            <div className={styles.addButtonWrap}>
              <Button
                purpose={ButtonPurpose.primary}
                size={ButtonSize.small}
                className={styles.addButton}
                onClick={onAdd}
                disabled={addDisabled}
                aria-describedby={
                  addDisabled && addDisabledReason
                    ? "speaker-upload-tooltip"
                    : undefined
                }
                icon={
                  <span className={styles.addIconWrap}>
                    <img
                      src={PlusSquareIcon}
                      alt=""
                      className={styles.addIcon}
                    />
                  </span>
                }
              >
                Add
              </Button>
              {addDisabled && addDisabledReason ? (
                <span
                  className={styles.addTooltip}
                  id="speaker-upload-tooltip"
                  role="tooltip"
                >
                  {addDisabledReason}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        {!hasAssignedSpeakers ? (
          <p className={styles.emptyState}>No Speakers Added</p>
        ) : showNoMatches ? (
          <p className={styles.emptyState}>No Matching Speakers</p>
        ) : (
          <ul
            className={[
              styles.list,
              draggingSpeakerId ? styles.listDragging : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-label="Assigned speakers list"
          >
            {filteredSpeakers.map((speaker) => (
              <li
                key={speaker.id}
                className={[
                  styles.listItem,
                  draggingSpeakerId === speaker.id ? styles.dragging : "",
                  dropTargetSpeakerId === speaker.id ? styles.dropTarget : "",
                  dropTargetSpeakerId === speaker.id &&
                  dropTargetPosition === "above"
                    ? styles.dropAbove
                    : "",
                  dropTargetSpeakerId === speaker.id &&
                  dropTargetPosition === "below"
                    ? styles.dropBelow
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                draggable
                onDragStart={() => handleDragStart(speaker.id)}
                onDragOver={(event) => handleDragOver(event, speaker.id)}
                onDrop={() => handleDrop(speaker.id)}
                onDragEnd={handleDragEnd}
              >
                {(() => {
                  const isCouple = speaker.imageType === "couple";

                  // Couples always expose both halves, even when either photo is missing.
                  const fallbackVariationOptions = isCouple
                    ? [
                        {
                          id: `${speaker.id}-a`,
                          photo: speaker.speakerAPhoto || "",
                          label: speaker.speakerAName || "Speaker A",
                        },
                        {
                          id: `${speaker.id}-b`,
                          photo: speaker.speakerBPhoto || "",
                          label: speaker.speakerBName || "Speaker B",
                        },
                      ]
                    : [];

                  const variationOptions =
                    speaker.variationOptions && speaker.variationOptions.length
                      ? speaker.variationOptions
                      : fallbackVariationOptions;

                  const selectedVariationIds =
                    speaker.selectedVariationIds &&
                    speaker.selectedVariationIds.length
                      ? speaker.selectedVariationIds
                      : variationOptions.map((option) => option.id);

                  return (
                    <SpeakerAssignRow
                      id={speaker.id}
                      photo={speaker.photo}
                      name={speaker.name}
                      variationOptions={variationOptions}
                      selectedVariationIds={selectedVariationIds}
                      onSelectedVariationIdsChange={(
                        nextSelectedVariationIds,
                      ) =>
                        onSpeakerVariationsChange(
                          speaker.id,
                          nextSelectedVariationIds,
                        )
                      }
                      onRemove={onRemove}
                      draggable
                    />
                  );
                })()}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

AssignSpeakersSection.propTypes = {
  speakers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      variationOptions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          photo: PropTypes.string,
          label: PropTypes.string.isRequired,
        }),
      ),
      selectedVariationIds: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  onSearch: PropTypes.func,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  onReorder: PropTypes.func,
  onSpeakerVariationsChange: PropTypes.func,
  addDisabled: PropTypes.bool,
  addDisabledReason: PropTypes.string,
};

AssignSpeakersSection.defaultProps = {
  speakers: [],
  onSearch: noop,
  onAdd: noop,
  onRemove: noop,
  onReorder: noop,
  onSpeakerVariationsChange: noop,
  addDisabled: false,
  addDisabledReason: "",
};

export default AssignSpeakersSection;
